import { createApi, type BaseQueryFn } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import type { AxiosRequestConfig, AxiosError } from "axios";

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use(async (config) => {
  const token = await (window as any).Clerk?.session?.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (!config.headers["X-Request-ID"]) {
    config.headers["X-Request-ID"] =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }
  if (!config.headers["X-User-Country"]) {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz === "Asia/Kolkata" || tz === "Asia/Calcutta") {
        config.headers["X-User-Country"] = "IN";
      }
    } catch {}
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const requestUrl = error.config?.url || "";
    const isPublicRoute = requestUrl.includes("/accept-invite");

    if (error.response?.status === 401 && !isPublicRoute) {
      console.warn("Session expired or unauthorized");

      // Clear invalid session
      await (window as any).Clerk?.signOut();

      // Redirect to sign-in page
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

export const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: "" },
  ): BaseQueryFn<
    | string
    | {
        url: string;
        method?: AxiosRequestConfig["method"];
        data?: AxiosRequestConfig["data"];
        params?: AxiosRequestConfig["params"];
        headers?: AxiosRequestConfig["headers"];
        body?: any;
      },
    unknown,
    unknown
  > =>
  async (args) => {
    try {
      const { url, method, data, params, headers, body } =
        typeof args === "string"
          ? {
              url: args,
              method: "GET",
              data: undefined,
              params: undefined,
              headers: undefined,
              body: undefined,
            }
          : args;

      const result = await axiosInstance({
        url: baseUrl + url,
        method: method || "GET",
        data: data || body,
        params,
        headers,
      });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

export const apiSlice = createApi({
  baseQuery: axiosBaseQuery(),
  tagTypes: ["Workspace"],
  endpoints: () => ({}),
});
